/** Site asset register (R-122) — the CMDB at the site tier (D-106). Local mode
 * hits gateway-local directly; cloud mode reads the same routes through the
 * /sites/{id} proxy. */

import type { ProcurementDomain } from '../procurement/procurement.types';

/** R-235: ONE state axis, the eight values the prototype defines. Renamed from
 *  `LifecycleState` along with the column — it is the same axis, recut.
 *
 *  `op_status` below is NOT part of it: a unit can be `in_use` and `faulty` at
 *  once, and that second axis is driven by real device liveness. */
export type AssetState =
  'in_stock' | 'reserved' | 'in_transit' | 'in_use' | 'in_maintenance' | 'retired' | 'disposed' | 'missing';

export type OpStatus = 'operational' | 'standby' | 'out_of_service' | 'faulty' | 'offline';

/** R-235: what is left of the substatus once `available` and `reserved` became
 *  states of their own. `null` is the ordinary case. */
export type AssetSubstatus = 'defective' | 'pending_disposal';
export type Criticality = 'critical' | 'high' | 'medium' | 'low';
export type AssetRelationKind = 'depends_on' | 'used_by' | 'part_of' | 'used_by_service' | 'replaces';

/** R-235: the prototype's TRANSITIONS table, mirrored from the backend's
 *  `allowed_transition`. The record's action buttons are BUILT from this, so an
 *  illegal move is never offered rather than being refused after the click.
 *
 *  ⚠ `reserved` is reachable from NOTHING here on purpose — nothing may
 *  hand-reserve a unit. Only an approved change writes it, and its outgoing hops
 *  are listed so releasing and installing stay legal. */
export const NEXT_STATES: Record<AssetState, AssetState[]> = {
  in_stock: ['in_transit', 'in_use', 'retired', 'missing'],
  reserved: ['in_stock', 'in_use'],
  in_transit: ['in_use', 'in_stock'],
  in_use: ['in_maintenance', 'in_stock', 'retired', 'missing'],
  in_maintenance: ['in_use', 'in_stock', 'retired'],
  retired: ['disposed', 'in_stock'],
  disposed: [],
  missing: ['in_use', 'in_stock', 'retired'],
};

/** Which KIND of location node each state may point at (R-235). One column here,
 *  not the prototype's two: warehouse-ness lives on the node
 *  (`locations.is_stockroom`), and the backend enforces it with a trigger.
 *
 *  The UI needs this to decide which picker to show — a stockroom list, the
 *  location tree, or both for the one flexible state. */
export const STOCK_STATES: AssetState[] = ['in_stock', 'reserved'];
export const PLACED_STATES: AssetState[] = ['in_use', 'in_transit'];
/** Either side is legal: sent away for repair, or repaired where it stands. */
export const FLEX_STATES: AssetState[] = ['in_maintenance'];

/** R-235: why a unit moved. Stable keys — the labels are translated, not stored.
 *  The last three are written by the change engine and are never offered to a
 *  human; `MANUAL_REASONS` is what a Move dialog may show. */
export const MOVEMENT_REASONS = [
  'registered',
  'issued_for_install',
  'installation',
  'issued_to_end_user',
  'transfer_between_locations',
  'returned_to_store',
  'fault_sent_to_maintenance',
  'repaired_and_returned',
  're_assigned',
  'end_of_life',
  'beyond_economic_repair',
  'disposal_confirmed',
  'stock_count_discrepancy',
  'found',
  're_tagged',
  'written_off',
  'reserved_for_change',
  'reservation_released',
  'replaced_removed_from_service',
] as const;
export type MovementReason = (typeof MOVEMENT_REASONS)[number];

const ENGINE_ONLY: MovementReason[] = [
  'reserved_for_change',
  'reservation_released',
  'replaced_removed_from_service',
];
export const MANUAL_REASONS: MovementReason[] = MOVEMENT_REASONS.filter((r) => !ENGINE_ONLY.includes(r));

/** How a unit entered the register. */
export type RegistrationMethod = 'scan' | 'receipt' | 'import' | 'manual';

/** R-235: where a class attribute's value lives — the decision that keeps the
 *  catalogue clean. Same for every unit of a SKU → the model; differs per unit →
 *  the asset; changes while it runs → the device record. */
export type LivesOn = 'model' | 'asset' | 'ci';

export interface SiteAsset {
  /** R-165: the catalogue product this unit is, if known. */
  model_id?: string | null;
  id: string;
  asset_tag: string;
  class_code: string;
  name: string;
  description: string;
  parent_asset_id: string | null;
  location_code: string | null;
  state: AssetState;
  /** R-235: only ever set while `state='in_stock'` (`defective`) or
   *  `state='retired'` (`pending_disposal`). */
  substatus: AssetSubstatus | null;
  op_status: OpStatus;
  criticality: Criticality;
  assigned_to: string | null;
  /** R-141: owning assignment group — wins incident routing. */
  managed_by_group: string | null;
  /** R-235: the responsible PERSON, which is a different question from which team
   *  owns it. The prototype's Assign dialog writes this one; a fault is routed to
   *  the group, but this is who you call. */
  managed_by: string | null;
  department: string | null;
  mc_ref: string | null;
  manufacturer: string | null;
  model: string | null;
  serial: string | null;
  /** R-234: why there is no serial, when there is none — `no_serial_printed`,
   *  `unreadable`, `sealed_unit`. Intake refuses to continue without one of the
   *  three, so a blank serial is never just an unanswered field. */
  serial_absent_reason: string | null;
  year_of_manufacture: number | null;
  supplier: string | null;
  /** R-168: the supplier-master row `supplier` resolved to, or null when the
   *  name is not in the master. Distinguishes an ATTRIBUTED vendor from a
   *  merely-typed one — only linked rows answer "every asset from this
   *  supplier". Never sent on a write: the server resolves the name. */
  supplier_id: string | null;
  po_ref: string | null;
  purchase_date: string | null;
  install_date: string | null;
  commissioning_date: string | null;
  warranty_expiry: string | null;
  acquisition_cost_cents: number | null;
  cost_center: string | null;
  depreciation_method: string | null;
  useful_life_years: number | null;
  ifrs_category: string | null;
  attributes: Record<string, unknown>;
  docs: AssetDoc[];
  maintenance_strategy: string | null;
  service_contract: string | null;
  last_serviced_at: string | null;
  next_due_at: string | null;
  notes: string | null;
  /** R-235: who booked this unit in, and by which path. Both null on rows written
   *  before the register asked. */
  registered_by: string | null;
  registration_method: RegistrationMethod | null;
  /** R-235: the goods receipt that created it, when procurement did. */
  receipt_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface AssetDoc {
  kind: 'photo' | 'datasheet' | 'drawing' | 'certificate' | 'po' | 'other';
  name: string;
  url?: string;
  object_key?: string;
  uploaded_at?: string;
}

export interface SiteAssetEvent {
  id: string;
  kind: string;
  actor_kind: 'user' | 'service';
  actor_id: string;
  payload: Record<string, unknown>;
  occurred_at: number;
}

export interface SiteAssetRelation {
  from_id: string;
  to_id: string;
  rel: AssetRelationKind;
}

export type SiteAssetDetail = SiteAsset & {
  events: SiteAssetEvent[];
  relations: SiteAssetRelation[];
};

/** Taxonomy option from the commons replica (class picker). */
export interface AssetClassOption {
  code: string;
  parent_code: string | null;
  level: 'group' | 'class' | 'type' | 'subtype';
  name_en: string;
  default_criticality: Criticality | null;
  useful_life_years: number | null;
  ifrs_category: string | null;
  /** R-234: which domains may raise a procurement request against this class.
   * Never empty — a class nobody may order is not expressible. */
  domains: ProcurementDomain[];
  /** R-235: units of this class report telemetry, so a device record is expected.
   *  False is what lets the record's telemetry tab say "none expected here"
   *  instead of showing an empty panel that reads like a dead feed. */
  is_connected_device: boolean;
  /** R-235: which custody question to ask. A phone expects an end user; an air
   *  handler expects a responsible person and no end user at all. Nudges only. */
  expects_end_user: boolean;
  expects_managed_by: boolean;
}

/** Class-specific attribute definition (schema-driven form, D-108). */
export interface AssetAttributeDef {
  key: string;
  label_en: string;
  data_type: 'text' | 'number' | 'bool' | 'date' | 'enum';
  enum_values: string[] | null;
  unit: string | null;
  mandatory: boolean;
  sort: number;
  /** FX-5: the dictionary "Default" — the create form prefills with it and the
   *  backend applies it when the attribute is omitted. */
  default_value: unknown | null;
  /** R-235: which form asks for this attribute. `model` values live on the SKU
   *  and are shown read-only on a unit; `asset` values are asked for at intake;
   *  `ci` belongs to the device record and nothing reads it yet. */
  lives_on: LivesOn;
}

/** R-235 — the body of the prototype's **Assign** dialog (V8:5114).
 *
 *  ⚠ **A FULL REPLACE of all four fields.** This IS the dialog: every one of them
 *  was on screen, so a blank (or omitted) field CLEARS. That is the only way to
 *  unassign a phone or drop a stale cost centre — and it is why assigning is its
 *  own endpoint rather than another branch of the edit form, which cannot express
 *  "nobody holds this any more".
 *
 *  The server appends a movement row (`reason: 're_assigned'`) ONLY when
 *  `assigned_to` actually changed. A cost-centre correction is not a handover. */
export interface AssignCustodyBody {
  /** Whoever is HOLDING it — the person you would ask for it back. Empty for fixed
   *  plant, which has no end user and simply sits in a room. */
  assigned_to: string | null;
  /** Whoever is technically RESPONSIBLE. A fault routes to the owning GROUP; this
   *  is who you call. */
  managed_by: string | null;
  cost_center: string | null;
  department: string | null;
}

export interface SiteAssetBody {
  class_code: string;
  /** R-165: the catalogue product this unit is. The server fills manufacturer,
   *  model, useful life, IFRS, criticality and maintenance strategy from it for
   *  every field left blank — an explicit value always wins. */
  model_id?: string | null;
  name: string;
  description?: string;
  parent_asset_id?: string | null;
  location_code?: string | null;
  criticality?: Criticality;
  assigned_to?: string | null;
  managed_by_group?: string | null;
  managed_by?: string | null;
  department?: string | null;
  mc_ref?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serial?: string | null;
  year_of_manufacture?: number | null;
  supplier?: string | null;
  po_ref?: string | null;
  purchase_date?: string | null;
  install_date?: string | null;
  commissioning_date?: string | null;
  warranty_expiry?: string | null;
  acquisition_cost_cents?: number | null;
  cost_center?: string | null;
  depreciation_method?: string | null;
  useful_life_years?: number | null;
  ifrs_category?: string | null;
  attributes?: Record<string, unknown>;
  /** R-241: the pre-printed label scanned at step 2. Its `asset_tag` becomes the
   *  asset's tag verbatim and the sticker is consumed in the same act — which is
   *  the whole point of scanning it. REQUIRED when `registration_method` is
   *  `scan`; omitted, a tag is minted from the class sequence instead. */
  label_id?: string | null;
  docs?: AssetDoc[];
  maintenance_strategy?: string | null;
  service_contract?: string | null;
  notes?: string | null;
  /** R-235, create only. `initial_state` is GONE: registration IS arrival in
   *  stock for both birth paths, and everything after it is a MOVE with a reason.
   *  This says which path, and the server stamps who from the token. */
  registration_method?: RegistrationMethod;
  receipt_id?: string | null;
}

/** R-235: one row of the append-only movement ledger — the prototype's Transfers
 *  table, and the record's Movement-history tab.
 *
 *  There is no write counterpart and there will not be: rows are appended by the
 *  backend inside the transaction that performs the move, so the ledger cannot
 *  disagree with what happened. */
export interface AssetMovement {
  id: string;
  /** `MV-0001` — the human reference in the table's first column. */
  reference: string;
  asset_id: string;
  asset_tag: string;
  from_location_code: string | null;
  to_location_code: string | null;
  /** null on exactly one row per asset: its registration, which comes from
   *  nowhere. */
  from_state: AssetState | null;
  to_state: AssetState;
  reason: MovementReason;
  notes: string | null;
  actor_kind: 'user' | 'service';
  moved_by: string;
  moved_at: number;
  linked_record_type: string | null;
  linked_record_id: string | null;
}

/** The Transfers screen's two filter dropdowns. Built from the rows that exist,
 *  so a filter never offers a value that matches nothing. */
export interface MovementFacets {
  reasons: MovementReason[];
  movers: string[];
}

/** R-235: the Move dialog, as a request. */
export interface AssetTransitionBody {
  state: AssetState;
  /** Only meaningful on a move to `in_stock`: flags the unit awaiting repair so
   *  the pool stops offering it. */
  substatus?: AssetSubstatus | null;
  assigned_to?: string | null;
  /** Required for `in_use`/`in_transit`, and for `in_stock` when the unit has no
   *  placement yet — the server refuses the move otherwise rather than failing on
   *  the placement trigger. */
  location_code?: string | null;
  /** One of `MANUAL_REASONS`. Omitted ⇒ the server picks the one the pressed
   *  button implies. */
  reason?: MovementReason;
  notes?: string | null;
  /** Both or neither — half a link points nowhere. */
  linked_record_type?: string | null;
  linked_record_id?: string | null;
  retirement_reason?: string | null;
  disposal_method?: string | null;
  disposal_vendor?: string | null;
  disposal_date?: string | null;
  checklist?: { item: string; checked: boolean }[];
}

/** R-235: one discovered controller in the Unregistered queue.
 *
 *  `status` is DERIVED by the backend, never stored: `pending` is a device no
 *  asset claims, `bound` IS `assets.mc_ref`, and only `rejected` is a row. */
export interface DiscoveredDevice {
  /** Controller MAC, lowercase hex — the same id space as `assets.mc_ref`. */
  device_ref: string;
  /** Always null today: our inventory wire carries no serial, which the screen
   *  renders as "none reported". The match rule that would use it is implemented
   *  and dormant. */
  reported_serial: string | null;
  firmware: string | null;
  source: string;
  first_seen: number | null;
  last_seen: number | null;
  /** How many sensor/actuator channels it announced — what says "real board"
   *  rather than a stray ping. */
  channels: number;
  status: 'pending' | 'bound' | 'rejected';
  asset_id: string | null;
  asset_tag: string | null;
  suggested_asset_id: string | null;
  suggested_asset_tag: string | null;
  /** `serial` | `device_id` | `asset_tag` — which rule proposed the match. */
  match_rule: string | null;
  rejected_note: string | null;
}

/** C-2 (R-147): one software/licence entry on an asset (notebook model, C.6).
 *  Manual entries only; soft-removed by id. */
export interface AssetSoftwareRecord {
  id: string;
  name: string;
  version: string | null;
  licence_ref: string | null;
  installed_at: string | null;
  note: string | null;
  created_at: number;
}

export interface AssetSoftwareBody {
  name: string;
  version?: string | null;
  licence_ref?: string | null;
  installed_at?: string | null;
  note?: string | null;
}

/** A-5 (R-147): a refrigerant handling entry (HVAC classes with a
 *  `refrigerant_charge` attribute). Surfaces in the asset event history
 *  (kind='refrigerant'). */
export type RefrigerantAction = 'recover' | 'recharge' | 'reclaim';
export interface RefrigerantLogBody {
  action: RefrigerantAction;
  qty_kg: number;
  technician: string;
  note?: string;
}

/** R-130: per-asset KPIs + the fleet class average (benchmarks replica). */
export interface AssetKpiBenchmark {
  avg_uptime_90d: number | null;
  avg_mtbf_hours: number | null;
  avg_mttr_hours: number | null;
  avg_incidents_qtr: number | null;
  sample_size: number;
  computed_at_ms: number;
}

export interface AssetKpisRecord {
  uptime_90d: number | null;
  mtbf_hours: number | null;
  mttr_hours: number | null;
  incidents_qtr: number;
  benchmark: AssetKpiBenchmark | null;
  /** Reliability rank within the sub-type class among in-service peers
   *  (slide 13 "3rd of 11"); null when the asset isn't in service. */
  rank_in_class: number | null;
  class_size: number | null;
}

/** Result of a bulk asset CSV import (per-row errors + minted tags). */
export interface AssetImportReport {
  dry_run: boolean;
  total_rows: number;
  applied: number;
  errors: { line: number; error: string }[];
  created_tags: string[];
}

/** R-165: a model as the site picker sees it — cloud canonical or a provisional
 *  this site authored offline. Both are usable; `provisional` is shown so the
 *  operator knows it has not reached the cloud yet. */
export interface SiteAssetModelOption {
  id: string;
  manufacturer: string;
  model_number: string;
  name: string;
  class_code: string;
  useful_life_years: number | null;
  ifrs_category: string | null;
  default_criticality: string | null;
  default_maintenance_strategy: string | null;
  specs: Record<string, unknown>;
  min_stock: number | null;
  /** approved | unapproved (cloud) | provisional (authored here) */
  stewardship: string;
  provisional: boolean;
  /** R-234. NULL ⇒ inherit the class's domains; a model may only ever NARROW
   * them, never widen. Procurement's line picker filters on this. */
  domains: ProcurementDomain[] | null;
  /** R-234. False hides the model from procurement entirely — it still exists
   * in the register, it just may not be bought. */
  orderable: boolean;
  /** R-234: the catalogue estimate in fils. Seeds the request line; the PO
   * carries what was actually paid. */
  unit_cost_cents: number | null;
}

/** A supplier as the site sees it (read-only mirror of the cloud master).
 *  `approved` is shown, never filtered on — see `useSiteSuppliers`. */
export interface SiteSupplierOption {
  id: string;
  name: string;
  contact: string;
  lead_time_days: number;
  approved: boolean;
}

export interface ProvisionalModelBody {
  manufacturer: string;
  model_number: string;
  name?: string;
  class_code: string;
  useful_life_years?: number | null;
  ifrs_category?: string | null;
  default_criticality?: string | null;
  default_maintenance_strategy?: string | null;
  min_stock?: number | null;
}

/** R-165 P4 — batch receive. `failed` is populated only when a unit failed AFTER
 *  pre-flight validation (a race); the units listed in `created` are real. */
export interface BatchReceiveReport {
  created: SiteAsset[];
  created_tags: string[];
  failed: string | null;
}

// ─── R-235: the QR label pool (the prototype's `v-qr`, three levels deep) ─────

/** One label. `asset_tag` becomes the asset's tag VERBATIM when it is consumed —
 *  the sticker and the register cannot disagree, because `assets.asset_tag` is
 *  unique too and this is the same string. */
export interface AssetLabel {
  id: string;
  asset_tag: string;
  class_code: string;
  batch_id: string | null;
  /** `unused → bound` (consumed by intake or a receipt) or `unused → void`
   *  (misprinted, lost, peeled off). Both ends are terminal, and voiding does NOT
   *  reuse the sequence number — a gap in the numbers is evidence, not a bug. */
  state: 'unused' | 'bound' | 'void';
  asset_id: string | null;
  bound_at: number | null;
  created_at: number;
}

/** One print run — which is what people actually handle: a sheet on a desk. */
export interface AssetLabelBatch {
  id: string;
  class_code: string;
  /** What was printed in the middle segment: the class's `tag_prefix`, else its
   *  code. Recorded rather than recomputed, so changing the class later cannot
   *  make an existing sheet unreadable. */
  prefix: string;
  count: number;
  /** The physical sticker. Null on sheets printed before the dialog asked — the UI
   *  shows nothing rather than a guess. */
  size: LabelSize | null;
  created_at: number;
}

export const LABEL_SIZES = ['38x19', '50x25', '25x12'] as const;
export type LabelSize = (typeof LABEL_SIZES)[number];

export interface AssetLabelStats {
  class_code: string;
  unused: number;
  bound: number;
  void: number;
}

/** What the next sheet would be numbered, minting nothing.
 *
 *  The reason this exists as its own call: the counter is ONE-WAY. Generating
 *  burns numbers out of the class's sequence and never rewinds, so a mistyped 500
 *  costs 500 tags permanently. Seeing `VFU01-SEN-0007 → VFU01-SEN-0506` before
 *  pressing Generate is what prevents that. */
export interface LabelSequencePreview {
  class_code: string;
  prefix: string;
  first: string;
  last: string;
}

export interface NewLabelBatchBody {
  class_code: string;
  count: number;
  size?: LabelSize;
}
